import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ProductCard from '@/components/base/ProductCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getProductsByCategory } from '@/mocks/products';
import { siteInfo } from '@/mocks/site';
import type { Product } from '@/mocks/products';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const allProducts = getProductsByCategory(slug || '');
  const [sortBy, setSortBy] = useState<'default' | 'price-asc' | 'price-desc' | 'sales'>('default');
  const [sortedProducts, setSortedProducts] = useState<Product[]>(allProducts);

  useEffect(() => {
    let sorted = [...allProducts];
    switch (sortBy) {
      case 'price-asc':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'sales':
        sorted.sort((a, b) => b.sales - a.sales);
        break;
      default:
        break;
    }
    setSortedProducts(sorted);
  }, [sortBy, slug]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-foreground-600 mb-4">未找到该分类</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: category.description,
    url: `${siteInfo.url}/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOMeta
        title={`${category.name} | 日本原装正品精选 - ${siteInfo.name}`}
        description={`${category.description}。${siteInfo.name}精选${category.productCount}+件${category.name}好物，日本原装正品，品质保障。`}
        keywords={`${category.name},日本${category.name},日本${category.name}代购,${siteInfo.keywords}`}
        canonicalUrl={`${siteInfo.url}/category/${category.slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionPageSchema) }}
      />

      <div className="pt-20 pb-4 bg-background-100">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{category.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Sort Bar */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-600">
              共 <strong className="text-foreground-900">{allProducts.length}</strong> 件商品
            </p>
            <div className="flex items-center gap-1">
              <span className="text-xs text-foreground-500 mr-2 hidden sm:inline">排序：</span>
              {([
                { value: 'default' as const, label: '默认' },
                { value: 'sales' as const, label: '销量' },
                { value: 'price-asc' as const, label: '价格↑' },
                { value: 'price-desc' as const, label: '价格↓' },
              ]).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                    sortBy === opt.value
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {sortedProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {allProducts.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂无商品</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}